// src/components/business/BusinessForm.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Building, FileText, MapPin, Globe, Phone, Mail, ChevronLeft, 
  Upload, AlertCircle, Check, X, Image, Briefcase, Facebook, Youtube, 
  Linkedin, Instagram, Twitter, DollarSign
} from 'lucide-react';
import { db, storage } from '../../firebase/config'; // Import Firebase modules
import { 
  collection, addDoc, deleteDoc, doc, getDoc, getDocs, updateDoc, 
  serverTimestamp, query, where 
} from 'firebase/firestore';
import { deleteObject, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext'; // Import auth context
import { currencyOptions } from '../../utils/currencyUtils';

const BusinessForm = ({ isEditing = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isDemoMode } = useAuth(); // Get current authenticated user
  
  const logoInputRef = useRef(null);
  const letterheadInputRef = useRef(null);
  const emailHeaderInputRef = useRef(null);
  const emailSignatureInputRef = useRef(null);

  // State for form data
  const [formData, setFormData] = useState({
    businessId: '',
    name: '',
    businessLogo: null,
    level: 'Headquarters',
    parentBusinessId: '',
    industry: '',
    category: 'Products',
    type: 'Physical',
    tel: '',
    mobile: '',
    email: '',
    address: '',
    country: '',
    currency: '',
    website: '',
    socialLinks: [],
    socialLinkUrls: {
      facebook: '',
      youtube: '',
      linkedin: '',
      instagram: '',
      tiktok: '',
      twitter: ''
    },
    bankingDetails: {
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      branch: ''
    },
    includeBankingInInvoice: false,
    letterhead: null,
    emailHeader: null,
    emailSignature: null
  });

  // State for image previews
  const [previews, setPreviews] = useState({
    logo: null,
    letterhead: null,
    emailHeader: null,
    emailSignature: null
  });

  // State for validation errors and loading state
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [parentBusinesses, setParentBusinesses] = useState([]);
  
  // State for file upload status
  const [uploadProgress, setUploadProgress] = useState({
    businessLogo: 0,
    letterhead: 0,
    emailHeader: 0,
    emailSignature: 0
  });

  // Business level options
  const levelOptions = ['Headquarters', 'Subsidiary', 'Branch', 'Section', 'Department'];
  
  // Category options
  const categoryOptions = ['Products', 'Services'];
  
  // Type options
  const typeOptions = ['Physical', 'Digital'];
  
  // Social media options
  const socialOptions = [
    { id: 'facebook', label: 'Facebook', icon: Facebook },
    { id: 'youtube', label: 'YouTube', icon: Youtube },
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
    { id: 'instagram', label: 'Instagram', icon: Instagram },
    { id: 'tiktok', label: 'TikTok', icon: Building }, // Using Building as placeholder for TikTok
    { id: 'twitter', label: 'Twitter', icon: Twitter }
  ];

  // Load existing business data if editing
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      
      // Fetch business data from Firestore
      const fetchBusinessData = async () => {
        try {
          const businessDoc = await getDoc(doc(db, 'businesses', id));
          
          if (businessDoc.exists()) {
            const businessData = businessDoc.data();
            
            // Format the data for our form
            setFormData({
              businessId: id,
              name: businessData.name || '',
              businessLogo: null, // We'll handle file data differently
              level: businessData.level || 'Headquarters',
              parentBusinessId: businessData.parentBusinessId || '',
              industry: businessData.industry || '',
              category: businessData.category || 'Products',
              type: businessData.type || 'Physical',
              tel: businessData.tel || '',
              mobile: businessData.mobile || '',
              email: businessData.email || '',
              address: businessData.address || '',
              country: businessData.country || '',
              currency: businessData.currency || '',
              website: businessData.website || '',
              socialLinks: businessData.socialLinks || [],
              socialLinkUrls: businessData.socialLinkUrls || {
                facebook: '',
                youtube: '',
                linkedin: '',
                instagram: '',
                tiktok: '',
                twitter: ''
              },
              bankingDetails: businessData.bankingDetails || {
                bankName: '',
                accountName: '',
                accountNumber: '',
                routingNumber: '',
                swiftCode: '',
                iban: '',
                branch: ''
              },
              includeBankingInInvoice: businessData.includeBankingInInvoice ?? false,
              letterhead: null,
              emailHeader: null,
              emailSignature: null
            });
            
            // Set image previews if URLs exist
            if (businessData.businessLogoUrl) {
              setPreviews(prev => ({ ...prev, logo: businessData.businessLogoUrl }));
            }
            if (businessData.letterheadUrl) {
              setPreviews(prev => ({ ...prev, letterhead: businessData.letterheadUrl }));
            }
            if (businessData.emailHeaderUrl) {
              setPreviews(prev => ({ ...prev, emailHeader: businessData.emailHeaderUrl }));
            }
            if (businessData.emailSignatureUrl) {
              setPreviews(prev => ({ ...prev, emailSignature: businessData.emailSignatureUrl }));
            }
          }
          
          setIsLoading(false);
        } catch (error) {
          console.error('Error fetching business data:', error);
          setIsLoading(false);
        }
      };
      
      fetchBusinessData();
    }
  }, [isEditing, id]);

  // Fetch potential parent businesses
  useEffect(() => {
    // Only fetch if the user is authenticated
    if (!currentUser) return;
    
    const fetchParentBusinesses = async () => {
      try {
        // Create a reference to the businesses collection
        const businessesRef = collection(db, 'businesses');
        
        // In a real app, you'd use a query to filter businesses
        // e.g., where('createdBy', '==', currentUser.uid)
        const businessesSnapshot = await getDocs(query(
          businessesRef,
          where('createdBy', '==', currentUser.uid),
          where('level', 'in', ['Headquarters', 'Subsidiary', 'Branch'])
        ));
        
        const businessesList = [];
        businessesSnapshot.forEach((doc) => {
          businessesList.push({
            id: doc.id,
            name: doc.data().name
          });
        });
        
        setParentBusinesses(businessesList);
      } catch (error) {
        console.error('Error fetching parent businesses:', error);
      }
    };
    
    // For demo purposes, we'll keep the mock data for now
    // In a real app, you'd call fetchParentBusinesses()
    setParentBusinesses([
      { id: '1', name: 'Global Industries' },
      { id: '2', name: 'Tech Solutions Inc.' },
      { id: '3', name: 'Innovate Enterprises' }
    ]);
  }, [currentUser]);

  // Handle text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBankingChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      bankingDetails: {
        ...prev.bankingDetails,
        [name]: value
      }
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Handle social media selection
  const handleSocialToggle = (socialId) => {
    setFormData(prev => {
      const socialLinks = [...prev.socialLinks];
      
      if (socialLinks.includes(socialId)) {
        // Remove if already selected
        return {
          ...prev,
          socialLinks: socialLinks.filter(id => id !== socialId)
        };
      } else {
        // Add if not already selected
        return {
          ...prev,
          socialLinks: [...socialLinks, socialId]
        };
      }
    });
  };

  // Handle social media URL input
  const handleSocialUrlChange = (socialId, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinkUrls: {
        ...prev.socialLinkUrls,
        [socialId]: value
      }
    }));
    
    // Clear error
    const errorKey = `socialLinkUrls.${socialId}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  // Handle radio button selection (level, category, type)
  const handleRadioChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Special logic when level changes
    if (name === 'level' && value === 'Headquarters') {
      // Headquarters can't have a parent
      setFormData(prev => ({
        ...prev,
        parentBusinessId: '',
        [name]: value
      }));
    }
    
    // Clear error
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Upload file to Firebase Storage
  const uploadFileToStorage = async (file, fieldName, businessId) => {
    return new Promise((resolve, reject) => {
      // Create a storage reference
      const storageRef = ref(storage, `businesses/${businessId}/${Date.now()}_${file.name}`);
      
      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      // Register observers
      uploadTask.on('state_changed', 
        (snapshot) => {
          // Track upload progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(prev => ({
            ...prev,
            [fieldName]: progress
          }));
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          // Get download URL when upload completes
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ downloadURL, storageRef: uploadTask.snapshot.ref });
        }
      );
    });
  };

  // Handle file uploads
  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Basic validation
    if (file.size > 5 * 1024 * 1024) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: 'File size should be less than 5MB'
      }));
      return;
    }
    
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/svg+xml'].includes(file.type)) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: 'Only JPG, JPEG, PNG and SVG formats are supported'
      }));
      return;
    }
    
    // Update form data with file
    setFormData(prev => ({
      ...prev,
      [fieldName]: file
    }));
    
    // Create and set preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews(prev => ({
        ...prev,
        [fieldName === 'businessLogo' ? 'logo' : fieldName]: reader.result
      }));
    };
    reader.readAsDataURL(file);
    
    // Clear error
    setFormErrors(prev => ({
      ...prev,
      [fieldName]: ''
    }));
  };

  // Remove a file/image
  const handleRemoveFile = (fieldName) => {
    const previewField = fieldName === 'businessLogo' ? 'logo' : fieldName;
    
    // Reset the input
    if (fieldName === 'businessLogo') logoInputRef.current.value = '';
    if (fieldName === 'letterhead') letterheadInputRef.current.value = '';
    if (fieldName === 'emailHeader') emailHeaderInputRef.current.value = '';
    if (fieldName === 'emailSignature') emailSignatureInputRef.current.value = '';
    
    // Update state
    setFormData(prev => ({
      ...prev,
      [fieldName]: null
    }));
    
    setPreviews(prev => ({
      ...prev,
      [previewField]: null
    }));
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.name.trim()) errors.name = 'Business name is required';
    if (!formData.level) errors.level = 'Business level is required';
    if (formData.level !== 'Headquarters' && !formData.parentBusinessId) {
      errors.parentBusinessId = 'Parent business is required for non-headquarters entities';
    }
    if (!formData.industry.trim()) errors.industry = 'Industry is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    
    // Format validations
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email format is invalid';
    }
    
    if (formData.website && !/^(http|https):\/\/[^ "]+$/.test(formData.website)) {
      errors.website = 'Website URL must start with http:// or https://';
    }
    
    // Validate social URLs if social networks are selected
    formData.socialLinks.forEach(socialId => {
      const url = formData.socialLinkUrls[socialId];
      if (!url || !url.trim()) {
        errors[`socialLinkUrls.${socialId}`] = `URL is required for ${socialId}`;
      } else if (!/^(http|https):\/\/[^ "]+$/.test(url)) {
        errors[`socialLinkUrls.${socialId}`] = `Invalid URL format for ${socialId}`;
      }
    });
    
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isDemoMode) {
      alert("Demo Mode Restriction:\nIn demo mode, data modifications are not saved. Sign up for a free account to use all features.");
      return;
    }
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Scroll to first error
      const firstError = document.querySelector('.text-red-600');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setIsLoading(true);
    
    let createdBusinessRef = null;
    const uploadedStorageRefs = [];

    try {
      const businessData = {
        name: formData.name,
        level: formData.level,
        industry: formData.industry,
        category: formData.category,
        type: formData.type,
        tel: formData.tel,
        mobile: formData.mobile,
        email: formData.email,
        address: formData.address,
        country: formData.country,
        currency: formData.currency,
        website: formData.website,
        socialLinks: formData.socialLinks,
        socialLinkUrls: formData.socialLinkUrls,
        bankingDetails: formData.bankingDetails,
        includeBankingInInvoice: formData.includeBankingInInvoice,
        updatedAt: serverTimestamp()
      };
      
      // Add parent business ID if applicable
      if (formData.level !== 'Headquarters' && formData.parentBusinessId) {
        businessData.parentBusinessId = formData.parentBusinessId;
      }

      let targetBusinessId = id;

      // Storage rules authorize against the business document, so a new business
      // must exist before any files are uploaded to its folder.
      if (!isEditing) {
        businessData.createdAt = serverTimestamp();
        businessData.createdBy = currentUser.uid;

        createdBusinessRef = await addDoc(collection(db, 'businesses'), businessData);
        targetBusinessId = createdBusinessRef.id;

        await updateDoc(createdBusinessRef, {
          businessId: targetBusinessId
        });
      }
      
      // Upload files and get URLs
      const fileUploadPromises = [];
      
      if (formData.businessLogo && formData.businessLogo instanceof File) {
        const uploadTask = uploadFileToStorage(formData.businessLogo, 'businessLogo', targetBusinessId)
          .then(({ downloadURL, storageRef }) => {
            businessData.businessLogoUrl = downloadURL;
            return storageRef;
          });
        fileUploadPromises.push(uploadTask);
      }
      
      if (formData.letterhead && formData.letterhead instanceof File) {
        const uploadTask = uploadFileToStorage(formData.letterhead, 'letterhead', targetBusinessId)
          .then(({ downloadURL, storageRef }) => {
            businessData.letterheadUrl = downloadURL;
            return storageRef;
          });
        fileUploadPromises.push(uploadTask);
      }
      
      if (formData.emailHeader && formData.emailHeader instanceof File) {
        const uploadTask = uploadFileToStorage(formData.emailHeader, 'emailHeader', targetBusinessId)
          .then(({ downloadURL, storageRef }) => {
            businessData.emailHeaderUrl = downloadURL;
            return storageRef;
          });
        fileUploadPromises.push(uploadTask);
      }
      
      if (formData.emailSignature && formData.emailSignature instanceof File) {
        const uploadTask = uploadFileToStorage(formData.emailSignature, 'emailSignature', targetBusinessId)
          .then(({ downloadURL, storageRef }) => {
            businessData.emailSignatureUrl = downloadURL;
            return storageRef;
          });
        fileUploadPromises.push(uploadTask);
      }
      
      // Wait for every upload so successful files can be cleaned up if another fails.
      const uploadResults = await Promise.allSettled(fileUploadPromises);
      uploadResults.forEach(result => {
        if (result.status === 'fulfilled') {
          uploadedStorageRefs.push(result.value);
        }
      });

      const failedUpload = uploadResults.find(result => result.status === 'rejected');
      if (failedUpload) {
        throw failedUpload.reason;
      }
      
      // Save to Firestore
      if (isEditing) {
        businessData.businessId = id; // Ensure businessId field matches document ID
        await updateDoc(doc(db, 'businesses', id), businessData);
      } else {
        await updateDoc(createdBusinessRef, businessData);
      }
      
      // Navigate back to the main business page
      navigate('/business');
    } catch (error) {
      console.error('Error saving business:', error);

      // Do not leave uploaded files or a partially-created business behind.
      await Promise.allSettled(uploadedStorageRefs.map(storageRef => deleteObject(storageRef)));
      if (createdBusinessRef) {
        try {
          await deleteDoc(createdBusinessRef);
        } catch (cleanupError) {
          console.error('Error cleaning up incomplete business:', cleanupError);
        }
      }

      // Handle error
      setFormErrors(prev => ({
        ...prev,
        submit: 'An error occurred while saving. Please try again.'
      }));
      setIsLoading(false);
    }
  };

  if (isLoading && isEditing) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xs shadow-md overflow-hidden">
      <div className="bg-white shadow-md px-6 py-4 text-gray-800">
        <h2 className="text-lg font-medium">
          {isEditing ? 'Edit Business' : 'Create New Business'}
        </h2>
        <p className="text-purple-500 text-sm">
          {isEditing 
            ? 'Update business information in the form below' 
            : 'Fill out the form below to create a new business entity'}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="p-6">
        <div className="mb-6">
          <button 
            type="button"
            onClick={() => navigate('/business')}
            className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to Businesses
          </button>
        </div>
        
        {/* Basic Information */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          
          {/* Business Logo Upload */}
          <div className="mb-6 flex flex-col sm:flex-row items-start gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Logo</label>
              <div className="relative">
                {previews.logo ? (
                  <div className="relative">
                    <img 
                      src={previews.logo} 
                      alt="Logo preview" 
                      className="w-auto h-32 object-contain border rounded-lg"
                    />
                    <button 
                      type="button" 
                      onClick={() => handleRemoveFile('businessLogo')}
                      className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 text-red-600 hover:bg-red-200"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div onClick={() => logoInputRef.current?.click()} className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#190192]">
                    <Upload size={24} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Upload Logo</span>
                  </div>
                )}
                
                <input 
                  type="file" 
                  ref={logoInputRef}
                  className="hidden" 
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml" 
                  onChange={(e) => handleFileUpload(e, 'businessLogo')}
                />
              </div>
              
              {formErrors.businessLogo && (
                <p className="mt-1 text-red-600 text-xs flex items-center">
                  <AlertCircle size={12} className="mr-1" /> {formErrors.businessLogo}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">Recommended size: 400x400px. Max 5MB.</p>
            </div>
            
            <div className="flex-1 w-full">
              {/* Business Name */}
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building size={18} className="text-gray-400" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`pl-10 block w-full rounded-lg border ${
                      formErrors.name ? 'border-red-300' : 'border-gray-300'
                    } py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]`}
                    placeholder="Enter business name"
                  />
                </div>
                {formErrors.name && (
                  <p className="mt-1 text-red-600 text-xs flex items-center">
                    <AlertCircle size={12} className="mr-1" /> {formErrors.name}
                  </p>
                )}
              </div>
              
              {/* Industry */}
              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                  Industry <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Briefcase size={18} className="text-gray-400" />
                  </div>
                  <input
                    id="industry"
                    name="industry"
                    type="text"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className={`pl-10 block w-full rounded-lg border ${
                      formErrors.industry ? 'border-red-300' : 'border-gray-300'
                    } py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]`}
                    placeholder="e.g., Technology, Healthcare, etc."
                  />
                </div>
                {formErrors.industry && (
                  <p className="mt-1 text-red-600 text-xs flex items-center">
                    <AlertCircle size={12} className="mr-1" /> {formErrors.industry}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Business Level Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Level <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {levelOptions.map((level) => (
                <div 
                  key={level}
                  onClick={() => handleRadioChange('level', level)}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    formData.level === level 
                      ? 'bg-purple-50 border-[#190192] text-[#190192]' 
                      : 'border-gray-200 hover:border-[#190192] hover:bg-purple-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{level}</span>
                    {formData.level === level && (
                      <Check size={16} className="text-[#190192]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            {formErrors.level && (
              <p className="mt-1 text-red-600 text-xs flex items-center">
                <AlertCircle size={12} className="mr-1" /> {formErrors.level}
              </p>
            )}
          </div>
          
          {/* Parent Business (only if not headquarters) */}
          {formData.level !== 'Headquarters' && (
            <div className="mb-4">
              <label htmlFor="parentBusinessId" className="block text-sm font-medium text-gray-700 mb-1">
                Parent Business <span className="text-red-500">*</span>
              </label>
              <select
                id="parentBusinessId"
                name="parentBusinessId"
                value={formData.parentBusinessId}
                onChange={handleInputChange}
                className={`block w-full rounded-lg border ${
                  formErrors.parentBusinessId ? 'border-red-300' : 'border-gray-300'
                } py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]`}
              >
                <option value="">Select Parent Business</option>
                {parentBusinesses.map((business) => (
                  <option key={business.id} value={business.id}>
                    {business.name}
                  </option>
                ))}
              </select>
              {formErrors.parentBusinessId && (
                <p className="mt-1 text-red-600 text-xs flex items-center">
                  <AlertCircle size={12} className="mr-1" /> {formErrors.parentBusinessId}
                </p>
              )}
            </div>
          )}
          
          {/* Business Type and Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <div className="flex space-x-4">
                {categoryOptions.map((category) => (
                  <div 
                    key={category}
                    onClick={() => handleRadioChange('category', category)}
                    className={`border rounded-lg px-4 py-2 cursor-pointer transition-colors flex items-center ${
                      formData.category === category 
                        ? 'bg-purple-50 border-[#190192] text-[#190192]' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border ${
                      formData.category === category
                        ? 'border-[#190192] bg-[#190192]'
                        : 'border-gray-400'
                    } flex items-center justify-center mr-2`}>
                      {formData.category === category && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span>{category}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <div className="flex space-x-4">
                {typeOptions.map((type) => (
                  <div 
                    key={type}
                    onClick={() => handleRadioChange('type', type)}
                    className={`border rounded-lg px-4 py-2 cursor-pointer transition-colors flex items-center ${
                      formData.type === type 
                        ? 'bg-purple-50 border-[#190192] text-[#190192]' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border ${
                      formData.type === type
                        ? 'border-[#190192] bg-[#190192]'
                        : 'border-gray-400'
                    } flex items-center justify-center mr-2`}>
                      {formData.type === type && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span>{type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Contact Information */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            {/* Phone */}
            <div>
              <label htmlFor="tel" className="block text-sm font-medium text-gray-700 mb-1">
                Telephone
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone size={18} className="text-gray-400" />
                </div>
                <input
                  id="tel"
                  name="tel"
                  type="tel"
                  value={formData.tel}
                  onChange={handleInputChange}
                  className="pl-10 block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                  placeholder="Enter telephone number"
                />
              </div>
            </div>
            
            {/* Mobile */}
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                Mobile
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone size={18} className="text-gray-400" />
                </div>
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="pl-10 block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                  placeholder="Enter mobile number"
                />
              </div>
            </div>
          </div>
          
          {/* Email */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={18} className="text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`pl-10 block w-full rounded-lg border ${
                  formErrors.email ? 'border-red-300' : 'border-gray-300'
                } py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]`}
                placeholder="Enter email address"
              />
            </div>
            {formErrors.email && (
              <p className="mt-1 text-red-600 text-xs flex items-center">
                <AlertCircle size={12} className="mr-1" /> {formErrors.email}
              </p>
            )}
          </div>
          
          {/* Address */}
          <div className="mb-4">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <div className="relative">
              <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                <MapPin size={18} className="text-gray-400" />
              </div>
              <textarea
                id="address"
                name="address"
                rows="3"
                value={formData.address}
                onChange={handleInputChange}
                className="pl-10 block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                placeholder="Enter address"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            {/* Country */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Globe size={18} className="text-gray-400" />
                </div>
                <input
                  id="country"
                  name="country"
                  type="text"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="pl-10 block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                  placeholder="Enter country"
                />
              </div>
            </div>
            
            {/* Currency */}
            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign size={18} className="text-gray-400" />
                </div>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  className="pl-10 block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                >
                  <option value="">Select currency</option>
                  {currencyOptions.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Website */}
          <div className="mb-4">
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Globe size={18} className="text-gray-400" />
              </div>
              <input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleInputChange}
                className={`pl-10 block w-full rounded-lg border ${
                  formErrors.website ? 'border-red-300' : 'border-gray-300'
                } py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]`}
                placeholder="https://www.example.com"
              />
            </div>
            {formErrors.website && (
              <p className="mt-1 text-red-600 text-xs flex items-center">
                <AlertCircle size={12} className="mr-1" /> {formErrors.website}
              </p>
            )}
          </div>
        </div>
        
        {/* Social Media Links */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Social Media Links</h3>
          
          {/* Social Media Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Social Networks
            </label>
            <div className="flex flex-wrap gap-2">
              {socialOptions.map((social) => (
                <div 
                  key={social.id}
                  onClick={() => handleSocialToggle(social.id)}
                  className={`border rounded-lg px-3 py-2 cursor-pointer transition-colors flex items-center ${
                    formData.socialLinks.includes(social.id) 
                      ? 'bg-purple-50 border-[#190192] text-[#190192]' 
                      : 'border-gray-200 hover:border-[#190192] hover:bg-purple-50'
                  }`}
                >
                  <social.icon size={16} className="mr-2" />
                  <span>{social.label}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Social Media URL Inputs */}
          {formData.socialLinks.length > 0 && (
            <div className="space-y-4">
              {formData.socialLinks.map((socialId) => {
                const social = socialOptions.find(s => s.id === socialId);
                return (
                  <div key={socialId}>
                    <label htmlFor={`social-${socialId}`} className="block text-sm font-medium text-gray-700 mb-1">
                      {social?.label} URL
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {social?.icon && <social.icon size={18} className="text-gray-400" />}
                      </div>
                      <input
                        id={`social-${socialId}`}
                        type="url"
                        value={formData.socialLinkUrls[socialId]}
                        onChange={(e) => handleSocialUrlChange(socialId, e.target.value)}
                        className={`pl-10 block w-full rounded-lg border ${
                          formErrors[`socialLinkUrls.${socialId}`] ? 'border-red-300' : 'border-gray-300'
                        } py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]`}
                        placeholder={`Enter ${social?.label} URL`}
                      />
                    </div>
                    {formErrors[`socialLinkUrls.${socialId}`] && (
                      <p className="mt-1 text-red-600 text-xs flex items-center">
                        <AlertCircle size={12} className="mr-1" /> {formErrors[`socialLinkUrls.${socialId}`]}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Branding Materials */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Branding Materials</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Letterhead */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Letterhead</label>
              <div className="relative">
                {previews.letterhead ? (
                  <div className="relative">
                    <img 
                      src={previews.letterhead} 
                      alt="Letterhead preview" 
                      className="w-full h-36 object-cover rounded-lg"
                    />
                    <button 
                      type="button" 
                      onClick={() => handleRemoveFile('letterhead')}
                      className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 text-red-600 hover:bg-red-200"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => letterheadInputRef.current?.click()} 
                    className="w-full h-36 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#190192]"
                  >
                    <Image size={24} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Upload Letterhead</span>
                  </div>
                )}
                
                <input 
                  type="file" 
                  ref={letterheadInputRef}
                  className="hidden" 
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml" 
                  onChange={(e) => handleFileUpload(e, 'letterhead')}
                />
              </div>
              
              {formErrors.letterhead && (
                <p className="mt-1 text-red-600 text-xs flex items-center">
                  <AlertCircle size={12} className="mr-1" /> {formErrors.letterhead}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">Max 5MB. JPG, PNG, or SVG.</p>
            </div>
            
            {/* Email Header */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Header</label>
              <div className="relative">
                {previews.emailHeader ? (
                  <div className="relative">
                    <img 
                      src={previews.emailHeader} 
                      alt="Email header preview" 
                      className="w-full h-36 object-cover rounded-lg"
                    />
                    <button 
                      type="button" 
                      onClick={() => handleRemoveFile('emailHeader')}
                      className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 text-red-600 hover:bg-red-200"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => emailHeaderInputRef.current?.click()} 
                    className="w-full h-36 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#190192]"
                  >
                    <Image size={24} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Upload Email Header</span>
                  </div>
                )}
                
                <input 
                  type="file" 
                  ref={emailHeaderInputRef}
                  className="hidden" 
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml" 
                  onChange={(e) => handleFileUpload(e, 'emailHeader')}
                />
              </div>
              
              {formErrors.emailHeader && (
                <p className="mt-1 text-red-600 text-xs flex items-center">
                  <AlertCircle size={12} className="mr-1" /> {formErrors.emailHeader}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">Max 5MB. JPG, PNG, or SVG.</p>
            </div>
            
            {/* Email Signature */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Signature</label>
              <div className="relative">
                {previews.emailSignature ? (
                  <div className="relative">
                    <img 
                      src={previews.emailSignature} 
                      alt="Email signature preview" 
                      className="w-full h-36 object-cover rounded-lg"
                    />
                    <button 
                      type="button" 
                      onClick={() => handleRemoveFile('emailSignature')}
                      className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 text-red-600 hover:bg-red-200"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => emailSignatureInputRef.current?.click()} 
                    className="w-full h-36 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#190192]"
                  >
                    <Image size={24} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Upload Email Signature</span>
                  </div>
                )}
                
                <input 
                  type="file" 
                  ref={emailSignatureInputRef}
                  className="hidden" 
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml" 
                  onChange={(e) => handleFileUpload(e, 'emailSignature')}
                />
              </div>
              
              {formErrors.emailSignature && (
                <p className="mt-1 text-red-600 text-xs flex items-center">
                  <AlertCircle size={12} className="mr-1" /> {formErrors.emailSignature}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">Max 5MB. JPG, PNG, or SVG.</p>
            </div>
          </div>
        </div>

        {/* Banking Details */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Banking Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                id="bankName"
                name="bankName"
                type="text"
                value={formData.bankingDetails.bankName}
                onChange={handleBankingChange}
                className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                placeholder="Enter bank name"
              />
            </div>

            <div>
              <label htmlFor="accountName" className="block text-sm font-medium text-gray-700 mb-1">
                Account Name
              </label>
              <input
                id="accountName"
                name="accountName"
                type="text"
                value={formData.bankingDetails.accountName}
                onChange={handleBankingChange}
                className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                placeholder="Account holder name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                id="accountNumber"
                name="accountNumber"
                type="text"
                value={formData.bankingDetails.accountNumber}
                onChange={handleBankingChange}
                className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                placeholder="Enter account number"
              />
            </div>

            <div>
              <label htmlFor="routingNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Routing Number
              </label>
              <input
                id="routingNumber"
                name="routingNumber"
                type="text"
                value={formData.bankingDetails.routingNumber}
                onChange={handleBankingChange}
                className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                placeholder="Enter routing number"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <label htmlFor="swiftCode" className="block text-sm font-medium text-gray-700 mb-1">
                SWIFT Code
              </label>
              <input
                id="swiftCode"
                name="swiftCode"
                type="text"
                value={formData.bankingDetails.swiftCode}
                onChange={handleBankingChange}
                className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                placeholder="Enter SWIFT/BIC"
              />
            </div>

            <div>
              <label htmlFor="iban" className="block text-sm font-medium text-gray-700 mb-1">
                IBAN
              </label>
              <input
                id="iban"
                name="iban"
                type="text"
                value={formData.bankingDetails.iban}
                onChange={handleBankingChange}
                className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                placeholder="Enter IBAN"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-1">
                Branch
              </label>
              <input
                id="branch"
                name="branch"
                type="text"
                value={formData.bankingDetails.branch}
                onChange={handleBankingChange}
                className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                placeholder="Enter branch"
              />
            </div>
          </div>

          <div className="flex items-start gap-3 mt-2">
            <input
              id="includeBankingInInvoice"
              name="includeBankingInInvoice"
              type="checkbox"
              checked={formData.includeBankingInInvoice}
              onChange={handleCheckboxChange}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-[#190192] focus:ring-[#190192]"
            />
            <label htmlFor="includeBankingInInvoice" className="text-sm text-gray-700">
              Include banking details in invoices and financial transactions
            </label>
          </div>
        </div>
        
        {/* Form submission error message */}
        {formErrors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm flex items-center">
              <AlertCircle size={16} className="mr-2" />
              {formErrors.submit}
            </p>
          </div>
        )}
        
        {/* Form Actions */}
        <div className="flex justify-end space-x-4 mt-10 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/business')}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className={`px-6 py-2.5 rounded-lg text-white font-medium flex items-center ${
              isLoading ? 'bg-purple-400 cursor-not-allowed' : 'bg-[#190192] hover:bg-[#2C0DB5]'
            }`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Check size={18} className="mr-2" />
                {isEditing ? 'Update Business' : 'Create Business'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessForm;
